import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useActivitiesStore } from '../../store/activitiesStore';
import { usePWAStore } from '../../store/pwaStore';
import { useTranslation } from 'react-i18next';
import { CollapsibleNav } from '../navigation/CollapsibleNav';

export const MobileSidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activities, setCurrentActivityId, removeActivity } = useActivitiesStore();
  const { isMobileSidebarOpen, setMobileSidebarOpen } = usePWAStore();

  const handleNavigate = () => {
    setMobileSidebarOpen(false);
  };

  return (
    <Transition.Root show={isMobileSidebarOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileSidebarOpen}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          {/* Sidebar Panel */}
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              {/* Close button */}
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>

              {/* Sidebar Content */}
              <div className="flex grow flex-col gap-y-2 overflow-y-auto bg-white dark:bg-gray-800 px-0 pb-4">
                {/* Logo */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <button
                    onClick={() => {
                      navigate('/dashboard');
                      handleNavigate();
                    }}
                    className="flex items-center hover:opacity-80 transition-opacity cursor-pointer w-full"
                  >
                    <img src="/dollar-sign.png" alt="Auxilio Finance" className="h-10 w-10" />
                    <span className="ml-2 text-xl font-bold text-amber-600 dark:text-amber-400">
                      Auxilio Finance
                    </span>
                  </button>
                </div>

                {/* Navigation */}
                <div className="flex-1 overflow-y-auto">
                  <CollapsibleNav onNavigate={handleNavigate} />
                </div>

                {/* Activity History */}
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 pt-4">
                  <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">
                    {t('common:sidebar.activities')}
                  </h3>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {activities.map((activity) => {
                      const colorClass = activity.activityType === 'financial-questions'
                        ? 'border-l-4 border-amber-500'
                        : 'border-l-4 border-gray-400';

                      const targetRoute = activity.activityType === 'financial-questions'
                        ? '/ask-questions'
                        : '/dashboard';

                      return (
                        <div
                          key={activity.id}
                          className={`flex items-center justify-between px-4 py-3 rounded-lg active:bg-gray-100 dark:active:bg-gray-700 transition-colors ${colorClass}`}
                        >
                          <button
                            onClick={() => {
                              setCurrentActivityId(activity.id);
                              navigate(targetRoute, { state: { loadActivityId: activity.id } });
                              handleNavigate();
                            }}
                            className="flex-1 text-left text-sm text-gray-700 dark:text-gray-300"
                          >
                            {activity.name}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(t('common:sidebar.deleteConfirm', { name: activity.name }))) {
                                removeActivity(activity.id);
                              }
                            }}
                            className="p-2 text-gray-400 active:text-red-500 transition-all"
                            title={t('common:buttons.delete')}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      );
                    })}
                    {activities.length === 0 && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 px-4 py-2">
                        {t('common:sidebar.noActivities')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
